/**
 * src/shell/agents/services/GitAccessController.ts
 *
 * Scoped Git & CI Access Controller with User-Delegated Token Scoping
 * and PII / Secret Redaction for Safe Ops Summarization.
 */

export interface GitCredential {
  provider: 'github' | 'gitlab' | 'mesh_internal';
  token: string;
  scope: string[]; // Explicit list of authorized repositories
  expiresAt: number;
}

export interface SanitizedCommit {
  hash: string;
  authorName: string;
  message: string;
  timestamp: string;
  filesChanged: number;
}

export class GitAccessController {
  private static instance: GitAccessController;
  private userCredentials: Map<string, GitCredential> = new Map();

  private constructor() {}

  public static getInstance(): GitAccessController {
    if (!GitAccessController.instance) {
      GitAccessController.instance = new GitAccessController();
    }
    return GitAccessController.instance;
  }

  /**
   * Registers a user-delegated scoped token for specific repository access.
   */
  public registerUserCredential(userId: string, credential: GitCredential): void {
    this.userCredentials.set(userId, credential);
  }

  /**
   * Verifies if a user has explicit access to read telemetry from a repo.
   */
  public verifyRepoAccess(userId: string, repoUrl: string): boolean {
    const cred = this.userCredentials.get(userId);
    if (!cred) {
      // Default sandbox/mock allows sovereign-mesh local repos
      return repoUrl.includes('sovereign-mesh') || repoUrl.includes('msng3r2');
    }
    if (Date.now() > cred.expiresAt) return false;
    return cred.scope.some((scopedRepo) => repoUrl.includes(scopedRepo));
  }

  /**
   * Scrubs PII, API tokens, IP addresses, and sensitive secrets from raw git/CI logs.
   */
  public scrubTelemetry(rawText: string): string {
    if (!rawText) return '';

    return rawText
      // Scrub email addresses
      .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '[EMAIL]')
      // Scrub IPv4 addresses
      .replace(/\b(?:\d{1,3}\.){3}\d{1,3}\b/g, '[IP]')
      // Scrub OpenAI / Anthropic / GitHub / Generic API keys
      .replace(/\b(?:sk|ghp|gho|glpat|AKIA)-[a-zA-Z0-9_-]{16,}\b/g, '[API_KEY]')
      // Scrub Bearer tokens
      .replace(/Bearer\s+[A-Za-z0-9-_=.]+/gi, 'Bearer [REDACTED_TOKEN]')
      // Scrub Private Keys
      .replace(/-----BEGIN (?:RSA )?PRIVATE KEY-----[\s\S]*?-----END (?:RSA )?PRIVATE KEY-----/g, '[REDACTED_PRIVATE_KEY]');
  }
}

export const gitAccessController = GitAccessController.getInstance();
